'use client';

import {
  useContext,
  useMemo,
  useState,
  useEffect,
  type FC,
  useRef,
  useCallback,
} from 'react';
import {
  Flex,
  Icon,
  Input,
  Select,
  Text,
  Pill,
  Button,
  ButtonGroup,
  Dialog,
} from '@/_components';
import { rangeParser } from '@/_utils/parse';
import { convertToRanges } from '@/_utils/documents';
import { getClasses } from '@/_utils/classnames';
import styles from './PageInputBox.module.css';
import type { PageInputBoxProps } from './PageInputBox.types';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import type { Question } from '@/_types/models/questionnaire';
import { BulkDocument } from '@/_types/models/documents';
import { useBulkUploadProcessor } from '@/_contexts/admin/BulkUploadProcessorProvider';

export const PageInputBox: FC<PageInputBoxProps> = ({
  currentlySelectedDoc,
  doc,
  documents = [],
  isLoading,
  setIsDragging,
  showPageChips,
  setCurrentlySelectedDoc,
  setDocuments,
  editingDocId,
}) => {
  const [differentiatorBeingFocused, setDifferentiatorBeingFocused] = useState<
    null | string
  >(null);
  const [isOtherEditing, setIsOtherEditing] = useState<boolean>(false);
  const [otherName, setOtherName] = useState<string>('');
  const [customDocName, setCustomDocName] = useState<string>('');
  const [forceSelect, setForceSelect] = useState<boolean>(false);
  // Right-side page list selection state
  const [chipDragStartY, setChipDragStartY] = useState<number | null>(null);
  const [chipDragStartX, setChipDragStartX] = useState<number | null>(null);
  const [chipSelectionRect, setChipSelectionRect] = useState<{
    top: number;
    height: number;
    left?: number;
    right?: number;
  } | null>(null);
  const [isNewDocOpen, setIsNewDocOpen] = useState<boolean>(false);
  const {
    selectedDocChipPages,
    setSelectedDocChipPages,
    clearDocChipSelection,
  } = useBulkUploadProcessor();
  const [newDocType, setNewDocType] = useState<string>('');
  const [newDocCustomType, setNewDocCustomType] = useState<string>('');
  const [newDocDifferentiator, setNewDocDifferentiator] = useState<string>('');
  const [typeSearch, setTypeSearch] = useState<string>('');
  const [typeResults, setTypeResults] = useState<
    Array<{ name: string; value: string }>
  >([]);
  const didCommitTypeRef = useRef<boolean>(false);
  const chipListRef = useRef<HTMLDivElement>(null);
  const dragPreviewRef = useRef<HTMLDivElement | null>(null);
  const [editingDocIdState, setEditingDocIdState] = useState<number | null>(
    null,
  );
  const updateChipSelectionFromClient = useCallback(
    (clientX: number, clientY: number) => {
      if (chipDragStartY == null || !chipListRef.current) return;
      const rect = chipListRef.current.getBoundingClientRect();
      const currentY = clientY - rect.top;
      const top = Math.min(chipDragStartY, currentY);
      const height = Math.abs(currentY - chipDragStartY);
      const currentX = clientX - rect.left;
      const startLeft = chipDragStartX ?? currentX;
      const startRight = chipDragStartX ?? currentX;
      const left = Math.min(startLeft, currentX);
      const right = Math.max(startRight, currentX);
      setChipSelectionRect({ top, height, left, right });
      const rows = Array.from(
        chipListRef.current.querySelectorAll('[data-chip-row]'),
      ) as HTMLDivElement[];
      const selected: number[] = [];
      rows.forEach((row) => {
        const pill = row.querySelector(
          '[data-chip-pill]',
        ) as HTMLElement | null;
        const target = pill ?? row;
        const r = target.getBoundingClientRect();
        const x = r.left - rect.left;
        const y = r.top - rect.top;
        const w = r.width;
        const h = r.height;
        const intersects = !(
          x > (right ?? x) ||
          x + w < (left ?? x) ||
          y > top + height ||
          y + h < top
        );
        if (intersects) {
          const pg = Number(row.dataset.page);
          if (!Number.isNaN(pg)) selected.push(pg);
        }
      });
      if (doc.id) setSelectedDocChipPages(doc.id, selected);
    },
    [chipDragStartY, chipDragStartX, doc.id, setSelectedDocChipPages],
  );

  useEffect(() => {
    setEditingDocIdState(
      typeof editingDocId === 'number' ? editingDocId : null,
    );
  }, [editingDocId]);

  useEffect(() => {
    const handleWindowMouseUp = () => {
      if (chipDragStartY != null || chipDragStartX != null) {
        setChipDragStartY(null);
        setChipDragStartX(null);
        setChipSelectionRect(null);
      }
    };
    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [chipDragStartY, chipDragStartX, doc.id, showPageChips, chipListRef]);

  useEffect(() => {
    if (chipDragStartY == null) return;
    const onMove = (e: MouseEvent) =>
      updateChipSelectionFromClient(e.clientX, e.clientY);
    const onUp = () => {
      setChipDragStartY(null);
      setChipDragStartX(null);
      setChipSelectionRect(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [chipDragStartY, updateChipSelectionFromClient]);
  const { questionnairePreview } = useContext(QuestionnaireContext);

  const allUploadQuestions: Array<Question> = useMemo(
    () =>
      questionnairePreview?.questionnaire_questions?.filter(
        (q: Question) => q.question_type === 'upload',
      ) || [],
    [questionnairePreview?.questionnaire_questions],
  );

  const suggestions: Array<string> = useMemo(
    () =>
      allUploadQuestions.flatMap((question: Question) => {
        return question.options || [];
      }),
    [allUploadQuestions],
  );

  const parseDocumentOption = (
    option: string,
  ): { name: string; differentiator: string } => {
    const match = option.match(/^(.+?)\s*\((.+?)\)$/);
    return {
      name: match?.[1]?.trim() ?? option.trim(),
      differentiator: match?.[2]?.trim() ?? '',
    };
  };

  const suggestionItems = useMemo(() => {
    const unique = Array.from(new Set(suggestions));
    const items = unique.map((s) => ({ name: s, value: s }));
    items.push({ name: 'Other', value: '__other__' });
    return items;
  }, [suggestions]);

  const isCustomName: boolean = useMemo(
    () => !suggestionItems.some((i) => i.value === (doc.name as string)),
    [suggestionItems, doc.name],
  );

  // Keep local state in sync when current document changes
  // Useful for custom-name input mode
  useEffect(() => {
    if (isCustomName) setCustomDocName((doc.name as string) || '');
    // do not update when typing in otherName/customDocName to avoid losing focus
    // this effect only syncs when doc changes from outside
  }, [isCustomName, doc.id, doc.differentiator, doc.name]);

  // Initialize and filter Type searchable input
  useEffect(() => {
    setTypeResults(suggestionItems.filter((i) => i.value !== '__other__'));
    const current = typeof doc.name === 'string' ? (doc.name as string) : '';
    setTypeSearch(current);
  }, [suggestionItems, doc.id, doc.name]);

  const handleDocTypeSelect = (selected: string) => {
    const { name, differentiator } = parseDocumentOption(selected);
    const newDocuments = documents.map((oldDoc) => {
      if (
        oldDoc.id === doc.id &&
        oldDoc.differentiator === doc.differentiator
      ) {
        return {
          ...oldDoc,
          name,
          detailed_name: name,
          differentiator: differentiator || oldDoc.differentiator,
        } as any;
      }
      return oldDoc;
    });
    setDocuments(newDocuments);
  };

  const onSaveOtherName = () => {
    const trimmed = (otherName || '').trim();
    if (!trimmed) return setIsOtherEditing(false);
    const newDocuments = documents.map((oldDoc) => {
      if (
        oldDoc.id === doc.id &&
        oldDoc.differentiator === doc.differentiator
      ) {
        return {
          ...oldDoc,
          name: trimmed,
          detailed_name: trimmed,
        } as any;
      }
      return oldDoc;
    });
    setDocuments(newDocuments);
    setIsOtherEditing(false);
  };

  const updateDocumentsDocName = (value: string) => {
    // Only update local field live; commit to documents on blur to avoid re-renders stealing focus
    setCustomDocName(value);
  };

  const isActive: boolean = Boolean(
    currentlySelectedDoc &&
      currentlySelectedDoc?.name === doc.name &&
      currentlySelectedDoc?.differentiator === doc.differentiator &&
      currentlySelectedDoc?.id === doc.id,
  );

  const updateDocumentsDifferentiator = (value: string) => {
    const newDocuments = documents.map((oldDoc) => {
      if (
        oldDoc.differentiator === doc.differentiator &&
        oldDoc.id === doc.id
      ) {
        return {
          ...oldDoc,
          differentiator: value,
        };
      }

      return oldDoc;
    });

    setDocuments(newDocuments);
  };

  // replaced by handleDocTypeSelect via Select

  const pageInputBoxClasses = getClasses('p-6', styles.panelDoc, {
    [styles.activePanelDoc]: isActive,
  });

  const selectDocAndScroll = (pageNumber?: number) => {
    setCurrentlySelectedDoc(doc);
    const page = pageNumber ?? rangeParser(doc.pageRanges || '')?.[0];
    if (page) {
      const el = document.querySelector(
        `img[alt="Page ${page}"]`,
      ) as HTMLElement | null;
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSelectDoc = () => selectDocAndScroll();

  const formatDragLabel = (pagesToMove: number[]): string => {
    const sorted = [...pagesToMove].sort((a, b) => a - b);
    if (sorted.length === 1) return `Page ${sorted[0]}`;
    if (sorted.length === 2) return `Pages ${sorted[0]} and ${sorted[1]}`;
    return `Pages ${sorted.slice(0, -1).join(', ')} and ${sorted[sorted.length - 1]}`;
  };

  const setDragPreview = (event: React.DragEvent, pagesToMove: number[]) => {
    try {
      let previewEl: HTMLDivElement;
      if (pagesToMove.length === 1) {
        const source = event.currentTarget as HTMLElement;
        previewEl = source.cloneNode(true) as HTMLDivElement;
        previewEl.style.pointerEvents = 'none';
        previewEl.style.position = 'fixed';
        previewEl.style.left = '-1000px';
        previewEl.style.top = '-1000px';
        previewEl.style.transform = 'scale(0.95)';
        previewEl.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)';
      } else {
        previewEl = document.createElement('div');
        previewEl.textContent = formatDragLabel(pagesToMove);
        previewEl.style.pointerEvents = 'none';
        previewEl.style.display = 'inline-flex';
        previewEl.style.alignItems = 'center';
        previewEl.style.padding = '4px 8px';
        // Match pill colors where possible
        const source = event.currentTarget as HTMLElement;
        let bg = '';
        let fg = '';
        const tryEls = [
          source,
          ...Array.from(
            source.querySelectorAll('*') as unknown as HTMLElement[],
          ),
        ];
        for (const el of tryEls) {
          const cs = getComputedStyle(el);
          const candidateBg = cs.backgroundColor;
          if (
            candidateBg &&
            candidateBg !== 'transparent' &&
            !candidateBg.endsWith(', 0)')
          ) {
            bg = candidateBg;
            fg = cs.color;
            break;
          }
        }
        previewEl.style.background = bg || 'var(--accent)';
        previewEl.style.color = fg || 'white';
        previewEl.style.borderRadius = '999px';
        previewEl.style.fontSize = '11px';
        previewEl.style.fontWeight = '700';
        previewEl.style.whiteSpace = 'nowrap';
        previewEl.style.maxWidth = '180px';
        previewEl.style.transform = 'scale(0.9)';
      }
      document.body.appendChild(previewEl);
      dragPreviewRef.current = previewEl;
      event.dataTransfer.setDragImage(previewEl, 12, 12);
    } catch {}
  };

  const unclassifyPage = (page: number) => {
    const next = documents.map((d: BulkDocument) => {
      if (d.id !== doc.id) return d;
      const pagesArr = Array.isArray(d.pages) ? d.pages : [];
      const filtered = pagesArr.filter((pg: number) => pg !== page);
      return {
        ...d,
        pages: filtered,
        pageRanges: convertToRanges(filtered).join(', '),
      } as BulkDocument;
    });
    setDocuments(next);
  };

  return (
    <Flex
      className={pageInputBoxClasses}
      isColumn
      gap={4}
      onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
        // Allow marquee to start from anywhere inside the document card when in page-chips view
        if (!showPageChips) return;
        if (e.button !== 0) return;
        const target = e.target as HTMLElement | null;
        if (target && target.closest('[data-chip-pill]')) return;
        // ignore interactions with inputs/selects/buttons
        if (
          target &&
          (target.tagName === 'INPUT' ||
            target.tagName === 'SELECT' ||
            target.tagName === 'BUTTON' ||
            target.closest('button'))
        ) {
          return;
        }
        // Ensure marquee start also selects this document to avoid mismatched doc context
        setCurrentlySelectedDoc(doc);
        const rect = chipListRef.current?.getBoundingClientRect();
        if (!rect) return;
        // Clamp start to chip list bounds so the marquee doesn't jump
        const startY = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
        const startX = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
        setChipDragStartY(startY);
        setChipDragStartX(startX);
        setChipSelectionRect({
          top: startY,
          height: 0,
          left: startX,
          right: startX,
        });
        if (doc.id) setSelectedDocChipPages(doc.id, []);
        // prevent triggering select-doc click when starting drag
        e.stopPropagation();
      }}
      onClick={handleSelectDoc}
    >
      <Flex gap={4}>
        {(!isCustomName || forceSelect) && (
          <Input
            manualDropDownWidth="420px"
            isDisabled={isLoading}
            label="Type"
            isLabelHidden={true}
            results={typeResults}
            value={typeSearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              setTypeSearch(val);
              const filtered = suggestions
                .filter((s) => s.toLowerCase().includes(val.toLowerCase()))
                .map((s) => ({ name: s, value: s }));
              setTypeResults(filtered);
              didCommitTypeRef.current = false;
            }}
            onResultSelect={(result: any) => {
              const val = result?.value || result?.name || '';
              if (!val) return;
              setTypeSearch(val);
              handleDocTypeSelect(val);
              setForceSelect(false);
              didCommitTypeRef.current = true;
            }}
            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                const val = (typeSearch || '').trim();
                if (val) {
                  handleDocTypeSelect(val);
                  setForceSelect(false);
                  didCommitTypeRef.current = true;
                }
              }
            }}
            onBlur={() => {
              // Defer revert a bit so clicks on the popover can fire first
              setTimeout(() => {
                if (!didCommitTypeRef.current) {
                  const current =
                    typeof doc.name === 'string' ? (doc.name as string) : '';
                  setTypeSearch(current);
                  setTypeResults(
                    suggestions.map((s) => ({ name: s, value: s })),
                  );
                }
                didCommitTypeRef.current = false;
              }, 150);
            }}
            placeholder="Type to search"
            type="text"
          />
        )}
        {isCustomName && !isOtherEditing && !forceSelect && (
          <Input
            isDisabled={isLoading}
            label="Custom document name"
            isLabelHidden={true}
            placeholder="Enter document name"
            type="text"
            value={customDocName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              updateDocumentsDocName(val);
              if (val.trim() === '') {
                setForceSelect(true);
              }
            }}
            onBlur={() => {
              const val = (customDocName || '').trim();
              const newDocuments = documents.map((oldDoc) => {
                if (
                  oldDoc.id === doc.id &&
                  oldDoc.differentiator === doc.differentiator
                ) {
                  return {
                    ...oldDoc,
                    name: val,
                    detailed_name: val,
                  } as any;
                }
                return oldDoc;
              });
              setDocuments(newDocuments);
            }}
          />
        )}
        {isOtherEditing && (
          <Flex gap={4} isYCentered>
            <Input
              isDisabled={isLoading}
              label="Custom document name"
              isLabelHidden={true}
              placeholder="Enter document name"
              type="text"
              value={otherName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setOtherName(e.target.value)
              }
              autoFocus
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') onSaveOtherName();
              }}
            />
            <ButtonGroup isSameLine>
              <Button
                intent="secondary"
                size="tiny"
                value="Save"
                onClick={onSaveOtherName}
              />
              <Button
                intent="danger-secondary"
                size="tiny"
                value="Cancel"
                onClick={() => setIsOtherEditing(false)}
              />
            </ButtonGroup>
          </Flex>
        )}

        <Input
          isDisabled={isLoading}
          label="Differentiator"
          isLabelHidden={true}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (typeof differentiatorBeingFocused === 'string') {
              return setDifferentiatorBeingFocused(e.target.value);
            }

            updateDocumentsDifferentiator(e.target.value);
          }}
          onFocus={(e: React.ChangeEvent<HTMLInputElement>) => {
            setDifferentiatorBeingFocused(e.target.value);
          }}
          onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
            updateDocumentsDifferentiator(e.target.value);
            setDifferentiatorBeingFocused(null);
          }}
          placeholder="Employer / Payer"
          type="text"
          value={
            typeof differentiatorBeingFocused === 'string'
              ? differentiatorBeingFocused
              : doc.differentiator
          }
        />
        <Icon
          name="trash"
          onClick={(event) => {
            event.stopPropagation();

            const newDocuments = documents.filter(
              (oldDoc) =>
                !(
                  oldDoc.differentiator === doc.differentiator &&
                  oldDoc.id === doc.id
                ),
            );

            setDocuments(newDocuments);
            setCurrentlySelectedDoc(null);
          }}
          size={16}
          color="critical"
        />
      </Flex>
      {typeof editingDocId === 'number' && editingDocId === doc.id && (
        <Text type="small" color="accent">
          Click page thumbnail to add or remove from document
        </Text>
      )}

      {showPageChips ? (
        <Flex gap={4} isYCentered={true}>
          <div
            ref={(el) => {
              chipListRef.current = el;
            }}
            className={styles.chipList}
            data-chip-list
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              // If mousedown began on a pill itself, don't start marquee and don't clear selection
              const target = e.target as HTMLElement | null;
              if (target && target.closest('[data-chip-pill]')) {
                return;
              }
              // Only allow marquee when this document is in edit mode
              if (
                typeof editingDocIdState === 'number' &&
                editingDocIdState !== doc.id
              ) {
                return;
              }
              // Select this document when initiating marquee inside its list
              setCurrentlySelectedDoc(doc);
              // Clear any other document's marquee/selection
              clearDocChipSelection();
              const rect = chipListRef.current?.getBoundingClientRect();
              if (!rect) return;
              const startY = e.clientY - rect.top;
              const startX = e.clientX - rect.left;
              setChipDragStartY(startY);
              setChipDragStartX(startX);
              setChipSelectionRect({
                top: startY,
                height: 0,
                left: startX,
                right: startX,
              });
              if (doc.id) setSelectedDocChipPages(doc.id, []);
              // Prevent the panel from also starting a global marquee
              e.stopPropagation();
            }}
            onMouseMove={(e) => {
              updateChipSelectionFromClient(e.clientX, e.clientY);
            }}
            onMouseUp={() => {
              setChipDragStartY(null);
              setChipDragStartX(null);
              setChipSelectionRect(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                if (doc.id) setSelectedDocChipPages(doc.id, []);
                setChipSelectionRect(null);
              }
            }}
            tabIndex={0}
          >
            {chipSelectionRect &&
              chipDragStartY != null &&
              chipDragStartX != null && (
                <div
                  className={styles.chipSelectionOverlay}
                  style={{
                    top: chipSelectionRect.top,
                    left: Math.min(
                      chipSelectionRect.left ?? 0,
                      chipSelectionRect.right ?? 0,
                    ),
                    width:
                      Math.abs(
                        (chipSelectionRect.right ?? 0) -
                          (chipSelectionRect.left ?? 0),
                      ) || '100%',
                    height: chipSelectionRect.height,
                  }}
                />
              )}
            {(doc.pages || []).map((p: number) => (
              <div
                key={p}
                data-chip-row
                data-page={p}
                className={`${styles.chipRow} ${styles.chipDraggable}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentlySelectedDoc(doc);
                  const el = document.querySelector(
                    `img[alt='Page ${p}']`,
                  ) as HTMLElement | null;
                  el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
              >
                <span
                  className={`${styles.chipPillWrap} ${((doc.id && selectedDocChipPages[doc.id]) || []).includes(p) ? styles.chipRowSelected : ''}`}
                  draggable
                  onMouseDown={(e) => {
                    // Prevent list-level mousedown from resetting selection when starting a drag on a pill
                    e.stopPropagation();
                  }}
                  onDragStart={(e) => {
                    const currentSelected =
                      (doc.id && selectedDocChipPages[doc.id]) || [];
                    const moving =
                      currentSelected.length > 0 && currentSelected.includes(p)
                        ? currentSelected
                        : [p];
                    if (moving.length > 1) {
                      e.dataTransfer.setData(
                        'application/x-page-numbers',
                        JSON.stringify(moving),
                      );
                    } else {
                      e.dataTransfer.setData(
                        'application/x-page-number',
                        String(moving[0]),
                      );
                    }
                    e.dataTransfer.effectAllowed = 'move';
                    setDragPreview(e, moving);
                    setIsDragging(true);
                  }}
                  onDragEnd={() => {
                    setIsDragging(false);
                    if (dragPreviewRef.current) {
                      document.body.removeChild(dragPreviewRef.current);
                      dragPreviewRef.current = null;
                    }
                  }}
                  data-chip-pill
                >
                  <Pill
                    intent="accent"
                    label={`Page ${p}`}
                    iconLeft="grab"
                    iconRight="x"
                    onRightClick={() => unclassifyPage(p)}
                  />
                </span>
              </div>
            ))}
            {(!doc.pages || doc.pages.length === 0) && (
              <Flex isYCentered isXCentered className={styles.emptyState}>
                <Text color="secondary" type="small">
                  No pages associated
                </Text>
              </Flex>
            )}
          </div>
        </Flex>
      ) : (
        <Flex gap={4} isYCentered={true}>
          <Text type="small" fontWeight="medium">
            Pages
          </Text>
          <Input
            isDisabled={isLoading}
            label="Page ranges"
            isLabelHidden={true}
            isActive={isActive}
            onFocus={() => {
              setCurrentlySelectedDoc(doc);
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const updatedDoc = {
                ...doc,
                pages: rangeParser(e.target.value),
                pageRanges: e.target.value,
              };

              setCurrentlySelectedDoc(updatedDoc);
              setDocuments(
                documents.map((d) =>
                  d.id === doc.id && d.differentiator === doc.differentiator
                    ? updatedDoc
                    : d,
                ),
              );
            }}
            placeholder="Ranges"
            type="text"
            value={doc.pageRanges}
          />
        </Flex>
      )}
      {isNewDocOpen && (
        <Dialog
          isOpen
          onClose={() => setIsNewDocOpen(false)}
          title="Create New Document"
        >
          <Dialog.Content>
            <Flex isColumn gap={8}>
              <Select
                label="Type"
                items={suggestionItems}
                selectedValue={newDocType || ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const val = e.target.value;
                  setNewDocType(val);
                  if (val !== '__other__') setNewDocCustomType('');
                }}
              />
              {newDocType === '__other__' && (
                <Input
                  label="Custom type"
                  placeholder="e.g., Additional attachment"
                  type="text"
                  value={newDocCustomType}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setNewDocCustomType(e.target.value)
                  }
                />
              )}
              <Input
                label="Differentiator"
                placeholder="Employer / Payer"
                type="text"
                value={newDocDifferentiator}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewDocDifferentiator(e.target.value)
                }
              />
            </Flex>
          </Dialog.Content>
          <Dialog.Footer>
            <Button
              intent="danger-secondary"
              value="Cancel"
              onClick={() => setIsNewDocOpen(false)}
            />
            <Button
              intent="primary"
              value="Create"
              onClick={() => {
                const typeName =
                  newDocType === '__other__'
                    ? (newDocCustomType || '').trim()
                    : newDocType;
                if (!typeName) return;
                const newDocId =
                  Math.max(0, ...documents.map((d: any) => Number(d.id) || 0)) +
                  1;
                const pagesSorted = [
                  ...((doc.id && selectedDocChipPages[doc.id]) || []),
                ].sort((a, b) => a - b);
                const newDoc = {
                  id: newDocId,
                  name: typeName,
                  detailed_name: typeName,
                  differentiator: (newDocDifferentiator || '').trim(),
                  pages: pagesSorted,
                  pageRanges: convertToRanges(pagesSorted).join(', '),
                } as BulkDocument;
                // Remove moved pages from all existing documents to avoid duplicates
                const toMove = new Set(pagesSorted);
                const updatedDocs = documents.map((d) => {
                  const existing = Array.isArray(d.pages) ? d.pages : [];
                  const filtered = existing.filter(
                    (p: number) => !toMove.has(p),
                  );
                  if (filtered.length === existing.length) return d;
                  return {
                    ...d,
                    pages: filtered,
                    pageRanges: convertToRanges(filtered).join(', '),
                  } as BulkDocument;
                });
                const next = [...updatedDocs, newDoc];
                setDocuments(next);
                setCurrentlySelectedDoc(newDoc);
                setIsNewDocOpen(false);
                if (doc.id) setSelectedDocChipPages(doc.id, []);
                setChipSelectionRect(null);
              }}
            />
          </Dialog.Footer>
        </Dialog>
      )}
    </Flex>
  );
};
